import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from '../../../redux/root';
import ModalWindow from '../../../atoms/Dialog';
import { setInspectorId, setShowBulkUpdatePopup } from '../../../redux/slices/bulkSlice';
import { dispatch } from '../../../redux/store';
import { setInspectorName } from '../../../redux/slices/inspectionsSlice';
import BulkInspectorDropdown from './BulkInspectorDropdown';
import { useSetBulkInspectionsMutation } from '../../../redux/rtkQuery/apiSlice';
import ModalInfo from '../../../molecules/ModalInfo';
import { setTableSelectedRows } from '../../../redux/slices/generalSlice';

const BulkScheduleBreadcrumbs = ({ getCount }) => {
  const { showBulkUpdatePopup, bulkDate, scheduledCount, bulkInspections, inspectorId, additionlScheduledCount } = useSelector(
    (s: RootState) => s.bulkSlice,
  );
  const { inspectorName } = useSelector((s: RootState) => s.inspections);
  const tableSelectedRows = useSelector((s: RootState) => s.general.tableSelectedRows);
  const [message, setMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [setBulkInspections] = useSetBulkInspectionsMutation();

  useEffect(() => {
    setMessage(`has no inspections`);
    if (scheduledCount !== -1 && inspectorName) {
      switch (scheduledCount) {
        case 1:
          setMessage(`already has 1 inspection`);
          break;
        default:
          setMessage(`already has ${scheduledCount} inspections`);
          break;
      }
    }
  }, [scheduledCount]);
  const handleClose = () => {
    dispatch(setShowBulkUpdatePopup(false));
    dispatch(setInspectorName(''));
    dispatch(setInspectorId(-1));
  };

  const onUpdateAll = async () => {
    const selectedRows = tableSelectedRows.filter(tr => tr.selected);
    const inspections = bulkInspections.reduce((arr, insp) => {
      const row = selectedRows.find(sr => sr.id === insp.id);
      if (row) {
        arr.push({
          inspectionId: insp.id,
          title_id: insp.title,
        });
      }
      return arr;
    }, []);
    const payload = {
      inspectorId,
      inspections,
    };
    await setBulkInspections(payload);
    const _tr = tableSelectedRows.map(r => ({ ...r, selected: false }));
    dispatch(setTableSelectedRows(_tr));
    handleClose();
    setShowSuccess(true);
  };

  const inspectionLabel = additionlScheduledCount === 1 ? 'inspection' : 'inspections';
  const totalAfter = scheduledCount + additionlScheduledCount;

  return (
    <TopWrapper>
      {showSuccess && <ModalInfo title="Success" timeToClose={5000} isError={false} onClose={() => setShowSuccess(false)} errorMsg="" />}
      {showBulkUpdatePopup && (
        <ModalWindow onClose={handleClose}>
          <ModalBody>
            <Header>
              <Eyebrow>Bulk action</Eyebrow>
              <Title>Bulk Update</Title>
              <Subtitle>
                Set <Strong>{additionlScheduledCount}</Strong>&nbsp;{inspectionLabel} to <StatusChip>Scheduled</StatusChip> on <Strong>{bulkDate || '—'}</Strong> and assign them to an inspector.
              </Subtitle>
            </Header>

            <Field>
              <FieldLabel>
                Inspector<Required>*</Required>
              </FieldLabel>
              <BulkInspectorDropdown row={{}} />
            </Field>

            {inspectorName && scheduledCount !== -1 && (
              <Callout>
                <CalloutTitle>Workload check</CalloutTitle>
                <CalloutBody>
                  <Strong>{inspectorName}</Strong> {message}. Adding these will bring the total to <Strong>{totalAfter}</Strong>.
                </CalloutBody>
              </Callout>
            )}

            <Footer>
              <SecondaryButton type="button" onClick={handleClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={onUpdateAll} disabled={!inspectorName}>
                Update all
              </PrimaryButton>
            </Footer>
          </ModalBody>
        </ModalWindow>
      )}
    </TopWrapper>
  );
};

export default BulkScheduleBreadcrumbs;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

const TopWrapper = styled.div`
  position: relative;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: ${FONT_STACK};
  color: var(--mainText);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 20px;
  margin-bottom: 22px;
  border-bottom: 1px solid var(--border);
`;

const Eyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Title = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.2;
`;

const Subtitle = styled.div`
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--neutral-600);
  margin-top: 4px;
`;

const Strong = styled.span`
  font-weight: 600;
  color: var(--mainText);
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  vertical-align: 1px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const Required = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const Callout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  background: var(--warning-50, #fff7ed);
  border: 1px solid var(--warning-100, #fed7aa);
  border-left: 3px solid var(--warning-500, #f59e0b);
  border-radius: 8px;
  margin-bottom: 22px;
`;

const CalloutTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--warning-700, #b45309);
`;

const CalloutBody = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-700);
  line-height: 1.5;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 18px;
  border-top: 1px solid var(--border);
`;

const buttonReset = `
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
  height: 40px;
  min-width: 120px;
  padding: 0 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const PrimaryButton = styled.button`
  ${buttonReset}
  border: none;
  background: var(--primary-600);
  color: var(--neutral-0);
  &:hover:not(:disabled) {
    background: var(--primary-700);
  }
  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  ${buttonReset}
  background: var(--neutral-0);
  border: 1px solid var(--border);
  color: var(--neutral-700);
  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
`;
